/* The vault viewer's item side panel: pinned price (autosaved), tagged people,
   and — admins only — the classification with Analyse / Re-analyse and an
   in-place editor that saves on change. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { ClassificationEditBody, TaggableUser, VaultContentItem } from '../../../../api/client';
import { useAutosave } from '../../../../hooks/useAutosave';
import { Btn, Icon, NumInput, SaveStatus, Spin, TextArea } from '../kit';
import { font } from '../style';
import { C } from '../tokens';

export function PanelRow({ label, help, children }: { label: ReactNode; help?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ font: font(500, 13), color: C.text }}>{label}</span>
        {help && <span style={{ font: font(400, 11.5), color: C.sub }}>{help}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>{children}</div>
    </div>
  );
}

/* ── Pinned price ───────────────────────────────────────────────── */

export function PinnedPrice({ accountId, item, onSaved }: { accountId: string; item: VaultContentItem; onSaved: (p: number | null, f: number | null) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [v, setV] = useState({ price: item.pinned_price ?? null, floor: item.pinned_floor ?? null });
  const auto = useAutosave({
    value: v,
    delay: 600,
    validate: (x) => (x.price !== null && x.floor !== null && x.floor > x.price ? t('viewer.floorAbovePrice') : null),
    save: async (x) => {
      const price = x.price && x.price > 0 ? x.price : null;
      const floor = price === null ? null : x.floor && x.floor > 0 ? x.floor : null;
      const u = await api.updateContentItem(accountId, item.id, { pinned_price: price, pinned_floor: floor });
      onSaved(u.pinned_price ?? null, u.pinned_floor ?? null);
    },
  });
  return (
    <PanelRow
      label={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{t('viewer.pinned')}<SaveStatus status={auto.status} error={auto.error} onRetry={auto.retry} /></span>}
      help={t('viewer.pinnedHelp')}
    >
      <NumInput value={v.price} onChange={(p) => setV((s) => ({ price: p, floor: p ? s.floor : null }))} min={0} max={99999} allowEmpty placeholder={t('viewer.price')} width={62} />
      <NumInput value={v.floor} onChange={(f) => setV((s) => ({ ...s, floor: f }))} min={0} max={99999} allowEmpty placeholder={t('viewer.floor')} width={62} disabled={!v.price} />
    </PanelRow>
  );
}

/* ── Tagged people ──────────────────────────────────────────────── */

export function TagPeople({ accountId, item, onChange }: { accountId: string; item: VaultContentItem; onChange: (ids: number[]) => void }) {
  const { t } = useTranslation('assistantCompact');
  const tagged = useMemo(() => item.tagged_user_ids ?? [], [item.tagged_user_ids]);
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const [list, setList] = useState<TaggableUser[]>([]);
  const [cache, setCache] = useState<Map<number, TaggableUser>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const disabled = !item.classified;
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState<CSSProperties | null>(null);

  const fetchUsers = useCallback(async (query: string) => {
    setLoading(true);
    setErr(null);
    try {
      const page = await api.listTaggableUsers(accountId, { q: query.trim() || undefined, limit: 50 });
      setList(page.items);
      setCache((m) => { const n = new Map(m); page.items.forEach((u) => n.set(u.id, u)); return n; });
    } catch (e) {
      setList([]);
      setErr(e instanceof Error ? e.message : t('viewer.tagsFailed'));
    } finally {
      setLoading(false);
    }
  }, [accountId, t]);

  // First page on focus (it hits OnlyFans live), then a debounced server search.
  useEffect(() => { if (focused && !list.length) void fetchUsers(''); }, [focused, list.length, fetchUsers]);
  useEffect(() => {
    if (!focused || q.trim().length < 2) return;
    const h = window.setTimeout(() => void fetchUsers(q), 350);
    return () => window.clearTimeout(h);
  }, [q, focused, fetchUsers]);

  const sugg = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((u) => !tagged.includes(u.id) && (!s || u.username.toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s))).slice(0, 8);
  }, [list, q, tagged]);
  const open = focused && (sugg.length > 0 || (q.trim().length >= 2 && !loading));

  // The row sits inside a clipping Card in a scrolling side panel, so the menu
  // is portalled and pinned to the input; it flips up when there's no room below.
  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 12;
      const up = below < 200 && r.top > below;
      setMenuPos({ left: r.left, width: r.width, maxHeight: Math.min(260, (up ? r.top : window.innerHeight - r.bottom) - 16), ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ font: font(500, 13), color: C.text }}>{t('viewer.tagged')} <span style={{ color: C.muted }}>{tagged.length}</span></span>
        <span style={{ font: font(400, 11.5), color: C.muted }}>{disabled ? t('viewer.analyseFirst') : t('viewer.taggedHelp')}</span>
        {loading && <Spin size={11} />}
      </div>
      {tagged.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {tagged.map((id) => {
            const u = cache.get(id);
            return (
              <span key={id} title={u ? `${u.name || u.username} (id ${id})` : `id ${id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 7px', borderRadius: 5, background: C.hover, border: '1px solid var(--om-border-hi)', font: font(500, 11.5), color: C.body }}>
                @{u?.username || id}
                <button type="button" onClick={() => onChange(tagged.filter((x) => x !== id))} style={{ border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', padding: 0, display: 'flex' }}><Icon name="x" size={11} /></button>
              </span>
            );
          })}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          placeholder={t('viewer.tagSearch')}
          style={{ width: '100%', height: 28, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: font(400, 12.5), outline: 'none' }}
        />
        {open && menuPos && createPortal(
          <div className="cpt-root" style={{ position: 'fixed', ...menuPos, zIndex: 1100, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', overflow: 'auto', padding: 4 }}>
            {!sugg.length ? <div style={{ padding: 10, font: font(400, 12), color: C.muted }}>{t('viewer.tagNone')}</div> : sugg.map((u) => (
              <button key={u.id} type="button" className="cpt-hover-row" onMouseDown={(e) => { e.preventDefault(); setCache((m) => new Map(m).set(u.id, u)); onChange([...tagged, u.id]); setQ(''); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px', border: 0, borderRadius: 6, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                {u.avatar_url ? <img src={u.avatar_url} alt="" width={22} height={22} style={{ borderRadius: '50%', objectFit: 'cover' }} /> : <span style={{ width: 22, height: 22, borderRadius: '50%', background: C.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><Icon name="user" size={12} /></span>}
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span style={{ font: font(500, 12.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.username}</span>
                  <span style={{ font: font(400, 11), color: C.muted }}>@{u.username}</span>
                </span>
                {u.is_identity_verified && <Icon name="check" size={12} color={C.purpleLt} />}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>
      {err && <span style={{ font: font(400, 11.5), color: C.purplePale }}>{err}</span>}
    </div>
  );
}

/* ── Classification (admin only) ────────────────────────────────── */

const RATING = ['soft', 'topless', 'nude', 'hardcore'];
const EXPOSURE = ['clothed', 'lingerie', 'implied_nude', 'topless', 'bottomless', 'fully_nude'];
const SCENES = ['solo', 'bg', 'gg', 'bbg', 'bgg', 'threesome', 'group_gangbang', 'orgy'];
const BUCKETS = ['micro', 'short', 'medium', 'long', 'extended'];
const ARRAYS = ['focus_body', 'present_body', 'acts', 'setting', 'outfit', 'vibe', 'kink_tags', 'partner_gender'] as const;
type ArrKey = (typeof ARRAYS)[number];

const gate = (vals: string[], i: number | null) => (i != null && i >= 0 && i < vals.length ? vals[i] ?? null : null);
const arr = (cls: Record<string, unknown> | null, k: string) => { const v = cls?.[k]; return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []; };

function toBody(it: VaultContentItem): ClassificationEditBody {
  const cls = it.classification;
  return {
    content_rating: gate(RATING, it.content_rating),
    exposure_level: gate(EXPOSURE, it.exposure_level),
    scene_type: it.scene_type || null,
    duration_bucket: it.duration_bucket || null,
    focus_body: arr(cls, 'focus_body'), present_body: arr(cls, 'present_body'), acts: arr(cls, 'acts'), setting: arr(cls, 'setting'),
    outfit: arr(cls, 'outfit'), vibe: arr(cls, 'vibe'), kink_tags: arr(cls, 'kink_tags'), partner_gender: arr(cls, 'partner_gender'),
    partner_count: typeof cls?.partner_count === 'number' ? (cls.partner_count as number) : null,
    face_visible: typeof cls?.face_visible === 'boolean' ? (cls.face_visible as boolean) : null,
    description: it.description || null,
  };
}

export function Classification({ accountId, item, busy, onAnalyse, onSaved }: { accountId: string; item: VaultContentItem; busy: boolean; onAnalyse: () => void; onSaved: (u: VaultContentItem) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('contentRequests');
  const [editing, setEditing] = useState(false);
  const an = item.classified && !busy;
  const b = toBody(item);
  const kv: [string, string | null][] = [
    [tc('content.fields.rating'), b.content_rating],
    [tc('content.fields.exposure'), b.exposure_level],
    [tc('content.fields.scene'), b.scene_type],
    [tc('content.fields.length'), b.duration_bucket],
    [tc('content.fields.partnerCount'), b.partner_count != null ? String(b.partner_count) : null],
    [tc('content.fields.faceVisible'), b.face_visible == null ? null : b.face_visible ? tc('content.yes') : tc('content.no')],
  ];
  const labels: Record<ArrKey, string> = {
    focus_body: tc('content.fields.focus'), present_body: tc('content.fields.present'), acts: tc('content.fields.acts'), setting: tc('content.fields.setting'),
    outfit: tc('content.fields.outfit'), vibe: tc('content.fields.vibe'), kink_tags: tc('content.fields.kink'), partner_gender: tc('content.fields.partners'),
  };
  return (
    <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('viewer.classification')}</span>
        <span style={{ padding: '2px 7px', borderRadius: 99, border: `1px solid ${C.tintLine}`, font: font(500, 10.5), color: C.purplePale, whiteSpace: 'nowrap' }}>{t('viewer.adminOnly')}</span>
        <span style={{ flex: 1 }} />
        {an && (
          <button type="button" title={editing ? t('viewer.doneEditing') : t('viewer.edit')} onClick={() => setEditing((e) => !e)} style={{ width: 26, height: 26, border: `1px solid ${editing ? C.purple : C.borderHi}`, borderRadius: 6, background: 'transparent', color: editing ? C.purpleLt : C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={editing ? 'check' : 'edit-2'} size={13} />
          </button>
        )}
        <Btn kind="tint" icon={busy ? undefined : 'zap'} height={26} disabled={busy} onClick={onAnalyse}>{busy ? <><Spin />{t('viewer.analysingBtn')}</> : item.classified ? t('viewer.reanalyse') : t('viewer.analyse')}</Btn>
      </div>
      {item.analysis_error && !busy && <span style={{ font: font(400, 11.5), color: C.purplePale }}>{t('viewer.lastFailed', { reason: item.analysis_error })}</span>}
      {busy && <div style={{ padding: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: `1px dashed ${C.tintLine45}`, borderRadius: 8, font: font(400, 12), color: C.purplePale }}><Spin />{t('viewer.analysingNote')}</div>}
      {!busy && !item.classified && <div style={{ padding: 18, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: 8, font: font(400, 12), color: C.muted }}>{t('viewer.notAnalysedNote')}</div>}
      {an && !editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {item.description && <p style={{ margin: 0, font: font(400, 12.5, 1.5), color: C.body }}>{item.description}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {kv.map(([k, v], j) => (
              <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', borderTop: j > 2 ? `1px solid ${C.divider}` : undefined, borderLeft: j % 3 ? `1px solid ${C.divider}` : undefined }}>
                <span style={{ font: font(400, 10.5), color: C.muted }}>{k}</span>
                <span style={{ font: font(500, 12.5), color: v ? C.text : C.muted }}>{v || '—'}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ARRAYS.map((k) => {
              const vals = b[k];
              if (!vals.length) return null;
              return (
                <div key={k} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ width: 78, flex: 'none', font: font(400, 11.5), color: C.muted }}>{labels[k]}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{vals.map((v) => <span key={v} style={{ padding: '2px 7px', borderRadius: 5, background: C.hover, border: '1px solid var(--om-border-hi)', font: font(500, 11.5), color: C.body }}>{v}</span>)}</div>
                </div>
              );
            })}
          </div>
          <span style={{ font: font(400, 11), color: C.muted }}>{tc('content.taggedBy', { source: item.tag_source || 'auto' })}</span>
        </div>
      )}
      {an && editing && <ClassEditor accountId={accountId} item={item} labels={labels} onSaved={onSaved} />}
    </div>
  );
}

function ClassEditor({ accountId, item, labels, onSaved }: { accountId: string; item: VaultContentItem; labels: Record<ArrKey, string>; onSaved: (u: VaultContentItem) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('contentRequests');
  const [d, setD] = useState<ClassificationEditBody>(() => toBody(item));
  const auto = useAutosave({
    value: d,
    save: async (body) => { const u = await api.updateContentClassification(accountId, item.id, { ...body, description: body.description?.trim() || null }); onSaved(u); },
  });
  const patch = (p: Partial<ClassificationEditBody>) => setD((s) => ({ ...s, ...p }));
  const sel = (label: string, value: string | null, opts: string[], on: (v: string | null) => void) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ font: font(400, 10.5), color: C.muted }}>{label}</span>
      <select value={value ?? ''} onChange={(e) => on(e.target.value || null)} style={{ height: 28, background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 6px', color: C.text, font: font(500, 12), outline: 'none' }}>
        <option value="">{tc('content.unset')}</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><SaveStatus status={auto.status} error={auto.error} onRetry={auto.retry} idleLabel /></div>
      <TextArea value={d.description ?? ''} onChange={(v) => patch({ description: v })} height={72} placeholder={tc('content.fields.description')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
        {sel(tc('content.fields.rating'), d.content_rating, RATING, (v) => patch({ content_rating: v }))}
        {sel(tc('content.fields.exposure'), d.exposure_level, EXPOSURE, (v) => patch({ exposure_level: v }))}
        {sel(tc('content.fields.scene'), d.scene_type, SCENES, (v) => patch({ scene_type: v }))}
        {sel(tc('content.fields.length'), d.duration_bucket, BUCKETS, (v) => patch({ duration_bucket: v }))}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ font: font(400, 10.5), color: C.muted }}>{tc('content.fields.partnerCount')}</span>
          <NumInput value={d.partner_count} onChange={(v) => patch({ partner_count: v })} min={0} max={20} int allowEmpty width="100%" />
        </label>
        {sel(tc('content.fields.faceVisible'), d.face_visible == null ? null : d.face_visible ? 'yes' : 'no', ['yes', 'no'], (v) => patch({ face_visible: v == null ? null : v === 'yes' }))}
      </div>
      {ARRAYS.map((k) => <Chips key={k} label={labels[k]} values={d[k]} onChange={(vals) => patch({ [k]: vals } as Partial<ClassificationEditBody>)} placeholder={t('viewer.addTag')} />)}
    </div>
  );
}

function Chips({ label, values, onChange, placeholder }: { label: string; values: string[]; onChange: (v: string[]) => void; placeholder: string }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const tag = draft.trim().toLowerCase().replace(/\s+/g, '_');
    if (tag && !values.includes(tag)) onChange([...values, tag]);
    setDraft('');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ width: 78, flex: 'none', font: font(400, 11.5), color: C.muted, paddingTop: 6 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', padding: 4, border: `1px solid ${C.border}`, borderRadius: 6, background: C.input }}>
        {values.map((v) => (
          <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 4px 1px 7px', borderRadius: 5, background: C.hover, border: '1px solid var(--om-border-hi)', font: font(500, 11.5), color: C.body }}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} style={{ border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', padding: 0, display: 'flex' }}><Icon name="x" size={10} /></button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } else if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1)); }}
          onBlur={add}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: 70, height: 20, border: 0, outline: 'none', background: 'transparent', color: C.text, font: font(400, 12) }}
        />
      </div>
    </div>
  );
}
